import { TOrderWithRelation } from "@/types/order-type";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  BadgeCheck,
  Calendar,
  Package,
  Phone,
  MapPin,
  Truck,
  Receipt,
} from "lucide-react";

interface ViewOrderDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  data?: TOrderWithRelation | null;
  onClose?: () => void;
}

// ---------- helpers ----------
const fmtMoney = (v: unknown, currency = "USD") => {
  const n = typeof v === "string" ? Number(v) : (v as number | undefined);
  if (n == null || Number.isNaN(n)) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    n
  );
};

const fmtDateTime = (iso?: string | Date) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtSince = (iso?: string | Date) => {
  if (!iso) return "-";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

export default function ViewOrderDialog({
  open,
  onOpenChange,
  data,
  onClose,
}: ViewOrderDialogProps) {
  const order = data?.order;
  const customer = data?.customer;
  const items = data?.orderItems ?? [];

  const created = order?.createdAt;

  // derive totals if not provided
  const computedSubtotal = items.reduce((acc, it) => {
    const q = Number(it.quantity ?? 0);
    const p = Number(it.price ?? 0);
    const line =
      typeof it.amount === "number" ? it.amount : Number(it.amount ?? q * p);
    return acc + (Number.isFinite(line) ? line : 0);
  }, 0);

  const subtotal = order?.subTotal ?? computedSubtotal;
  const deliveryFee = order?.deliveryFee ?? 0;
  const discountAmount = order?.discountPercentage ?? 0;
  const total =
    order?.totalAmount ??
    Number(subtotal) + Number(deliveryFee) - Number(discountAmount);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange?.(o);
        if (!o) onClose?.();
      }}
    >
      <DialogContent className="max-w-3xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-emerald-600" />
              Order #{String(order?.id ?? "-")}
            </span>
            <div className="flex items-center gap-2">
              {order?.status && (
                <Badge variant="secondary">{order.status}</Badge>
              )}
              {order?.paymentStatus && (
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                  {order.paymentStatus}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Read-only order details
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[80vh] px-6 pb-2">
          {/* Customer & meta */}
          <Card className="mt-2">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 min-w-[220px]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center overflow-hidden">
                    {customer?.profileImageUrl ? (
                      <img
                        src={customer.profileImageUrl}
                        alt={customer?.name ?? "customer"}
                        className="w-10 h-10 object-cover"
                      />
                    ) : (
                      <span className="text-white font-semibold">
                        {customer?.name?.[0] ?? "?"}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {customer?.name ?? "-"}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Customer since{" "}
                      {customer?.createdAt && fmtDateTime(customer?.createdAt).split(",")[0]}
                    </div>
                  </div>
                </div>

                <Separator
                  orientation="vertical"
                  className="h-10 hidden sm:block"
                />

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">
                      {customer?.phone || "Not provided"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Shipping Address
                    </p>
                    <p className="text-sm text-gray-600 whitespace-pre-line max-w-[28rem]">
                      {customer?.address || "Not provided"}
                    </p>
                  </div>
                </div>

                <div className="ml-auto text-right min-w-[180px]">
                  <div className="text-xs text-muted-foreground">Created</div>
                  <div className="text-sm font-medium">
                    {fmtDateTime(created!)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmtSince(created!)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-4 w-4" />
                <div className="text-sm font-semibold">Items</div>
              </div>

              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">No items.</p>
              ) : (
                <div className="border rounded-lg divide-y">
                  <div className="grid grid-cols-12 gap-3 px-3 py-2 text-xs text-muted-foreground">
                    <div className="col-span-6">Product</div>
                    <div className="col-span-2 text-right">Qty</div>
                    <div className="col-span-2 text-right">Unit</div>
                    <div className="col-span-2 text-right">Amount</div>
                  </div>
                  {items.map((it, idx) => {
                    const qty = Number(it.quantity ?? 0);
                    const price = Number(it.price ?? 0);
                    const line =
                      it.amount != null ? Number(it.amount) : qty * price;
                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-12 gap-3 px-3 py-2 text-sm"
                      >
                        <div
                          className="col-span-6 truncate"
                          title={it.productName ?? String(it.productId ?? "-")}
                        >
                          {it.productName ?? it.productId ?? "-"}
                        </div>
                        <div className="col-span-2 text-right">
                          {Number.isFinite(qty) ? qty : "-"}
                        </div>
                        <div className="col-span-2 text-right">
                          {fmtMoney(price)}
                        </div>
                        <div className="col-span-2 text-right font-medium">
                          {fmtMoney(line)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shipping / Payment / Meta */}
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Truck className="h-4 w-4" /> Shipping
                </div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Tracking</span>
                  <span
                    className="truncate max-w-[160px]"
                    title={order?.trackingNumber || "NA"}
                  >
                    {order?.trackingNumber ?? "-"}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Receipt className="h-4 w-4" /> Payment
                </div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span>{order?.paymentStatus ?? "-"}</span>
                </div>
                <div className="text-sm flex justify-between">
                  <span className="text-muted-foreground">Source</span>
                  <span>{order?.source ?? "-"}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Notes</div>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap min-h-[48px]">
                  {order?.notes || "-"}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Totals */}
          <Card className="mt-4 mb-2">
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{fmtMoney(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery Fee</span>
                <span>{fmtMoney(deliveryFee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Discount</span>
                <span>
                  {order?.discountPercentage != null &&
                    order.discountPercentage !== "0"
                    ? `${order.discountPercentage}%`
                    : fmtMoney(discountAmount)}
                </span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold text-base">
                <span>Total</span>
                <span>{fmtMoney(total)}</span>
              </div>
            </CardContent>
          </Card>
        </ScrollArea>

        <DialogFooter className="px-6 pb-6">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
